import { AuthApi } from '@/utilities/api/AuthApi';
import React, { useState } from 'react';
import { Stack, Typography, Box, Button, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Routes } from '@/constants/Routes';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useRetailerMetadata } from '@/hooks/useRetailer';
import { AppConfig } from '@/constants/AppConfig';
import { VSpacer } from '@/components/DesignSystem';
import BlackFullLogo from '@/assets/logos/black/app_full_black.svg';
import { validatePassword } from '@shared/utilities';
import { PasswordInputFields } from './PasswordInputFields';

export const PasswordResetPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isMobile } = useMediaQuery();
  const { metadata } = useRetailerMetadata();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const retailerImageUrl = metadata?.image
    && `${AppConfig.staticImageHost}/${metadata.image}`;
  const isLoyaltyProgramDomain = window.location.hostname.includes(AppConfig.shortUrlDomain);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t($ => $.auth.passwordReset.passwordsDoNotMatch));
      return;
    }

    if (!validatePassword(password)) {
      setError(t($ => $.auth.passwordReset.passwordRequirementsNotMet));
      return;
    }

    if (!token) {
      setError(t($ => $.auth.passwordReset.invalidToken));
      return;
    }

    setLoading(true);

    try {
      await AuthApi.resetPassword({
        token,
        newPassword: password,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate(Routes.LOGIN);
      }, 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : t($ => $.auth.passwordReset.genericError));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Stack
        direction="row"
        justifyContent="center"
        paddingTop={isMobile ? '0px' : '100px'}
      >
        <Stack display="flex" flex={1} maxWidth="512px">
          <Stack
            alignItems="center"
            borderRadius="24px"
            boxShadow={
              '0px 1px 2px 0px rgba(0, 0, 0, 0.07),'
              + '0px 2px 4px 0px rgba(0, 0, 0, 0.07),'
              + '0px 4px 8px 0px rgba(0, 0, 0, 0.07),'
              + '0px 8px 16px 0px rgba(0, 0, 0, 0.07),'
              + '0px 16px 32px 0px rgba(0, 0, 0, 0.07),'
              + '0px 32px 64px 0px rgba(0, 0, 0, 0.07)'
            }
            padding="60px 24px 40px 24px"
            width="560px"
          >
            <Alert severity="success" sx={{ mb: 2 }}>
              {t($ => $.auth.passwordReset.successMessage)}
            </Alert>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      justifyContent="center"
      paddingTop={isMobile ? '0px' : '100px'}
    >
      <Stack display="flex" flex={1} maxWidth="512px">
        <Stack
          alignItems="center"
          borderRadius="24px"
          boxShadow={
            '0px 1px 2px 0px rgba(0, 0, 0, 0.07),'
            + '0px 2px 4px 0px rgba(0, 0, 0, 0.07),'
            + '0px 4px 8px 0px rgba(0, 0, 0, 0.07),'
            + '0px 8px 16px 0px rgba(0, 0, 0, 0.07),'
            + '0px 16px 32px 0px rgba(0, 0, 0, 0.07),'
            + '0px 32px 64px 0px rgba(0, 0, 0, 0.07)'
          }
          padding="60px 24px 40px 24px"
          width="560px"
        >
          {(isLoyaltyProgramDomain) ? (
            <>
              {!!retailerImageUrl && (
                <img
                  alt={`${metadata?.name} logo`}
                  height={40}
                  src={retailerImageUrl}
                />
              )}
            </>
          ) : (
            <img
              alt="logo"
              src={BlackFullLogo}
              style={{
                height: '40px',
                width: '205px',
                objectFit: 'contain',
              }}
            />
          )}
          <VSpacer size="8" />

          <Typography
            align="center"
            component="h1"
            gutterBottom
            variant="h4"
          >
            {token
              ? t($ => $.auth.passwordReset.title)
              : t($ => $.auth.passwordReset.createNewTitle)}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Typography gutterBottom variant="body1">
              {t($ => $.auth.passwordReset.instruction)}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <PasswordInputFields
              confirmPassword={confirmPassword}
              disabled={loading}
              newPassword={password}
              onConfirmPasswordChange={setConfirmPassword}
              onNewPasswordChange={setPassword}
            />

            <Button
              disabled={loading || !password || !confirmPassword}
              fullWidth
              size="large"
              sx={{ mt: 3 }}
              type="submit"
              variant="contained"
            >
              {loading
                ? t($ => $.auth.passwordReset.submitting)
                : t($ => $.auth.passwordReset.submit)}
            </Button>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};
